import { createSlice } from "@reduxjs/toolkit";
import {
  RECEIVED,
  applyMessageReceived,
  applyMessageSendFailed,
  applyMessageSendRetried,
  applyMessagesLoaded,
} from "./threadMessages";

// The client's one thread (pooled inbox SP1). Messages are stored newest-first
// as the API pages them; selectMessagesOldestFirst reverses for display.
// SP2 (W1): the message-list reducer bodies live in threadMessages.js, shared
// with inboxSlice; this slice's behaviour and exports are unchanged.
const initialState = {
  thread: null,
  advisors: [],
  messages: [],
  nextBefore: null,
  unread: false,
  status: "idle",
  sendErrors: {},
};

const computeUnread = (thread) =>
  !!thread?.lastAdvisorMessageAt &&
  (!thread.lastReadAt ||
    new Date(thread.lastAdvisorMessageAt) > new Date(thread.lastReadAt));

const threadSlice = createSlice({
  name: "thread",
  initialState,
  reducers: {
    threadLoading(state) {
      state.status = "loading";
    },
    threadLoaded(state, { payload: { thread, advisors } }) {
      state.thread = thread;
      state.advisors = advisors || [];
      state.status = "ready";
      state.unread = computeUnread(thread);
    },
    threadFailed(state) {
      state.status = "error";
    },
    messagesLoaded(state, { payload }) {
      applyMessagesLoaded(state, payload);
    },
    messageReceived(state, { payload }) {
      // Only a genuinely new message moves the unread clock — not the 201
      // that replaces an optimistic bubble, and not a duplicate echo.
      if (applyMessageReceived(state, payload) !== RECEIVED.INSERTED) return;
      if (payload.senderType === "advisor" && state.thread) {
        state.thread.lastAdvisorMessageAt = payload.createdAt;
        state.unread = computeUnread(state.thread);
      }
    },
    messageSendFailed(state, { payload }) {
      applyMessageSendFailed(state, payload);
    },
    // Removes the failed bubble and its error in one step — it does not hand
    // the composer anything back. The caller (tap-to-retry) must read the
    // failed message's text (or its own sendErrors[tempId]) *before*
    // dispatching this, or the text is gone once this runs.
    messageSendRetried(state, { payload: tempId }) {
      applyMessageSendRetried(state, tempId);
    },
    markedRead(state, { payload: lastReadAt }) {
      // SP2-W15: a late "thread read" event (another tab, or the server's
      // own broadcast arriving after a newer local mark-read) must not move
      // this backwards and re-light a badge that a more recent read already
      // cleared.
      if (
        state.thread &&
        (!state.thread.lastReadAt ||
          new Date(lastReadAt) > new Date(state.thread.lastReadAt))
      ) {
        state.thread.lastReadAt = lastReadAt;
      }
      state.unread = computeUnread(state.thread);
    },
    threadCleared() {
      return initialState;
    },
  },
});

export const {
  threadLoading,
  threadLoaded,
  threadFailed,
  messagesLoaded,
  messageReceived,
  messageSendFailed,
  messageSendRetried,
  markedRead,
  threadCleared,
} = threadSlice.actions;

export const selectThread = (s) => s.thread.thread;
// SP2-W14: has the client's thread ever been loaded into this store? Lets a
// mount-time effect (e.g. DashboardSidebar's live badge) decide whether it
// needs to fetch, without re-deriving thread status semantics.
export const selectThreadLoaded = (s) => s.thread.thread !== null;
export const selectAdvisors = (s) => s.thread.advisors;
export const selectMessagesOldestFirst = (s) =>
  [...s.thread.messages].reverse();
export const selectUnread = (s) => s.thread.unread;
export const selectThreadStatus = (s) => s.thread.status;

export default threadSlice.reducer;
